import { useEffect, useRef, useState } from 'react';
import { creerDepuisTexte, lireDepuisTexte } from '../services/contratService';
import RelectureDossier, { manquesRelecture } from './RelectureDossier';
import { getFormations } from '../services/formationService';
import SelectRecherche from '../components/SelectRecherche';
import BarreProgression, { useProgression } from '../components/BarreProgression';
import ChampMentions from '../components/ChampMentions';
import '../assets/styles/DevisPanel.css';
import '../assets/styles/Parametrage.css';

/**
 * « Créer un contrat / une convocation / une feuille de présence » depuis un
 * texte collé : un seul champ, le texte. Le serveur le lit (IA), retrouve
 * le client, les formations, les dates et les participants, puis pose la
 * chaîne d'un trait — demande, devis, contrat, journées, documents.
 *
 * `entite` dit ce qu'on attend du texte : un contrat se contente d'une
 * formation ; une convocation exige des dates et des participants ; une
 * feuille de présence, des dates.
 */
const LIBELLES = {
  contrat: {
    titre: 'Créer un contrat',
    bouton: 'Créer le contrat',
    fichier: 'Le contrat reçu',
    aide:
      'Collez un mail, un devis ou une convention : au minimum la société (ou l’interlocuteur) et la formation.',
    exemple:
      'Société ACME — SIRET 123 456 789 00012\nInterlocutrice : Marie Dupont, marie.dupont@acme.fr\nFormation SSCT (moins de 50 salariés), 3 jours, du 3 au 5 mars 2026, dans nos locaux à Lyon\nMontant : 4 500 € HT\nParticipants : DUPONT Marie (titulaire), MARTIN Paul (suppléant)…',
  },
  convocation: {
    titre: 'Créer une convocation',
    bouton: 'Créer la convocation',
    fichier: 'La convocation reçue',
    aide:
      'Collez le texte de la séance : formation, dates, lieu et participants.',
    exemple:
      'Formation SSCT — ACME (marie.dupont@acme.fr)\nLes 3, 4 et 5 mars 2026, 9h-17h, 12 rue des Lilas, Lyon\nParticipants :\nMarie DUPONT, titulaire, marie.dupont@acme.fr\nPaul MARTIN, suppléant, paul.martin@acme.fr\nFormateur : Jean Durand',
  },
  attestation: {
    titre: 'Créer une attestation',
    bouton: 'Créer l’attestation',
    fichier: 'L’attestation reçue',
    aide:
      'Collez le texte de la séance : formation, dates et participants (une attestation est nominative).',
    exemple:
      'Attestation de formation — Formation SSCT, ACME\nLes 3, 4 et 5 mars 2026, Lyon\nMarie DUPONT, titulaire, marie.dupont@acme.fr\nPaul MARTIN, suppléant, paul.martin@acme.fr\nFormateur : Jean Durand',
  },
  demande: {
    titre: 'Créer une demande de devis',
    bouton: 'Créer la demande',
    fichier: 'La demande ou le devis reçu',
    aide:
      'Collez le mail ou la demande du client : sa société, son interlocuteur et les formations souhaitées. Vous relisez avant la création.',
    exemple:
      'De : marie.dupont@acme.fr (ACME, 80 salariés)\nBonjour, nous souhaiterions un devis pour la formation SSCT, 3 jours, 4 élus, en intra à Lyon.\nEn option : une journée de formation économique.',
  },
  feuille_presence: {
    titre: 'Créer une feuille de présence',
    bouton: 'Créer la feuille de présence',
    fichier: 'La feuille de présence reçue',
    aide:
      'Collez le texte de la séance : au minimum la formation et ses dates.',
    exemple:
      'Feuille de présence — Formation économique du CSE\nACME, 3 et 4 mars 2026, Lyon\nDUPONT Marie\nMARTIN Paul\nFormateur : Jean Durand',
  },
};

function CreerDepuisTextePanel({ entite = 'contrat', onClose, onCree }) {
  const libelles = LIBELLES[entite] || LIBELLES.contrat;
  const [texte, setTexte] = useState('');
  // Fichiers joints (facultatifs) : le contrat, la convocation ou la feuille
  // reçus. Lus avec le texte, puis rangés au dossier de la demande.
  const [fichiers, setFichiers] = useState([]);
  const fichierRef = useRef(null);
  const [enCours, setEnCours] = useState(false);
  const [erreur, setErreur] = useState(null);
  const [resultat, setResultat] = useState(null);
  const progression = useProgression(enCours, { estimeeMs: 35000 });

  const pret = Boolean(texte.trim()) || fichiers.length > 0;

  // Pour un texte qui ne nomme pas la formation (un simple planning de dates) :
  // celle choisie ici sert de repli. Une formation nommée dans le texte prime.
  const [formations, setFormations] = useState([]);
  const [formationId, setFormationId] = useState('');
  useEffect(() => {
    let vivant = true;
    getFormations({ actif: true })
      .then((res) => vivant && setFormations(res.data || []))
      .catch(() => vivant && setFormations([]));
    return () => {
      vivant = false;
    };
  }, []);

  function ajouterFichiers(e) {
    const choisis = Array.from(e.target.files || []);
    setFichiers((prev) => [...prev, ...choisis].slice(0, 5));
    if (fichierRef.current) fichierRef.current.value = '';
  }

  // Premier temps : l'IA lit et propose le dossier ; rien n'est créé. La
  // proposition se relit et se corrige, ou se prend telle quelle.
  const [proposition, setProposition] = useState(null);

  async function lire(e) {
    e.preventDefault();
    if (!pret) return;
    setEnCours(true);
    setErreur(null);
    try {
      const res = await lireDepuisTexte(texte, entite, fichiers, { formationId });
      setProposition(res.data);
    } catch (err) {
      setErreur(err.message || 'Lecture impossible.');
    } finally {
      setEnCours(false);
    }
  }

  // Second temps : le dossier tel qu'il a été relu.
  async function creer() {
    setEnCours(true);
    setErreur(null);
    try {
      const res = await creerDepuisTexte(texte, entite, fichiers, { proposition });
      setResultat(res);
      if (onCree) onCree(res);
    } catch (err) {
      setErreur(err.message || 'Création impossible.');
    } finally {
      setEnCours(false);
    }
  }

  const manquesAVoir = proposition ? manquesRelecture(proposition, entite) : [];

  return (
    <div className="devis-modal-overlay" onClick={enCours ? undefined : onClose}>
      <div
        className={`devis-modal${proposition && !resultat ? '' : ' devis-modal-etroit'}`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="devis-modal-header">
          <div>
            <div className="devis-modal-numero">{libelles.titre}</div>
            <div className="devis-modal-sub">
              {proposition && !resultat
                ? 'Voici ce que l’IA a lu : corrigez si besoin, puis créez'
                : 'Depuis un texte collé et/ou un fichier — l’IA lit, vous relisez avant la création'}
            </div>
          </div>
          <div className="devis-modal-header-right">
            <button
              type="button"
              className="devis-drawer-close"
              onClick={onClose}
              aria-label="Fermer"
              disabled={enCours}
            >
              ×
            </button>
          </div>
        </div>

        <div className="devis-modal-body">
          {erreur && <div className="devis-alert devis-alert-erreur">{erreur}</div>}

          {resultat ? (
            <>
              <div className="devis-alert devis-alert-succes">{resultat.message}</div>
              {resultat.data?.lecture?.resume && (
                <p className="param-hint">Ce que l’IA a lu : {resultat.data.lecture.resume}</p>
              )}
              <div className="devis-modal-actions">
                <button type="button" className="devis-btn devis-btn-primary" onClick={onClose}>
                  Fermer
                </button>
              </div>
            </>
          ) : proposition ? (
            <>
              {proposition.resume && <p className="param-hint">Ce que l’IA a lu : {proposition.resume}</p>}
              {proposition.remarques?.length > 0 && (
                <div className="devis-alert">
                  {proposition.remarques.map((r, i) => (
                    <div key={i}>{r}</div>
                  ))}
                </div>
              )}
              {manquesAVoir.length > 0 && (
                <div className="devis-alert devis-alert-erreur">
                  À compléter pour créer {libelles.titre.replace('Créer ', '').toLowerCase()} :{' '}
                  {manquesAVoir.join(' ; ')}.
                </div>
              )}
              <RelectureDossier
                entite={entite}
                proposition={proposition}
                onChange={setProposition}
                formations={formations}
                disabled={enCours}
              />
              {enCours && <BarreProgression valeur={progression} libelle="Création du dossier" estimation />}
              <div className="devis-modal-actions">
                <button type="button" className="devis-btn" onClick={() => setProposition(null)} disabled={enCours}>
                  ← Revenir au texte
                </button>
                <button
                  type="button"
                  className="devis-btn devis-btn-primary"
                  onClick={creer}
                  disabled={enCours || manquesAVoir.length > 0}
                >
                  {enCours ? 'Création en cours' : libelles.bouton}
                </button>
              </div>
            </>
          ) : (
            <form onSubmit={lire}>
              <p className="param-hint">{libelles.aide}</p>
              <label className="devis-form-field">
                <span>Texte à lire</span>
                <ChampMentions
                  rows={16}
                  value={texte}
                  onChange={(e) => setTexte(e.target.value)}
                  suffixe={'\n'}
                  placeholder={libelles.exemple}
                  disabled={enCours}
                  autoFocus
                />
              </label>

              <label className="devis-form-field">
                <span>Formation (facultatif : sinon retrouvée par les dates du texte)</span>
                <SelectRecherche
                  value={formationId}
                  onChange={(e) => setFormationId(e.target.value)}
                  disabled={enCours}
                >
                  <option value="">— Celle du texte ou des dates —</option>
                  {formations.map((f) => (
                    <option key={f.id} value={String(f.id)}>
                      {f.code} — {f.intitule}
                    </option>
                  ))}
                </SelectRecherche>
              </label>

              <label className="devis-form-field">
                <span>
                  {libelles.fichier} (facultatif — PDF ou Word, 5 au plus)
                </span>
                <input
                  ref={fichierRef}
                  type="file"
                  accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                  multiple
                  onChange={ajouterFichiers}
                  disabled={enCours || fichiers.length >= 5}
                />
              </label>
              {fichiers.length > 0 && (
                <ul className="creer-fichiers">
                  {fichiers.map((f, i) => (
                    <li key={`${f.name}-${i}`}>
                      <span>{f.name}</span>
                      <button
                        type="button"
                        className="devis-btn devis-btn-sm"
                        onClick={() => setFichiers((prev) => prev.filter((_, k) => k !== i))}
                        disabled={enCours}
                      >
                        Retirer
                      </button>
                    </li>
                  ))}
                </ul>
              )}
              <p className="param-hint">
                Texte ou fichier : l’un des deux suffit.
              </p>
              <p className="param-hint">
                {texte.length.toLocaleString('fr-FR')} caractères. L’IA propose, vous relisez et
                corrigez avant la création ; rien n’est envoyé au client.
              </p>
              {enCours && <BarreProgression valeur={progression} libelle="Lecture par l’IA" estimation />}
              <div className="devis-modal-actions">
                <button type="button" className="devis-btn" onClick={onClose} disabled={enCours}>
                  Annuler
                </button>
                <button
                  type="submit"
                  className="devis-btn devis-btn-primary"
                  disabled={enCours || !pret}
                >
                  {enCours ? 'Lecture par l’IA…' : 'Lire le texte'}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}

export default CreerDepuisTextePanel;
